import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { API } from "../App";

const LOGO_URL =
  "https://static.prod-images.emergentagent.com/jobs/203f7def-ad2c-4f8f-807f-2bc10f2f16d6/images/a913d16ba95302b639f95999ec0d2a7b5d654d2a1647e0be42e9b257d09c7d8a.png";

/**
 * ForgotPasswordPage — single page that works for every account type
 * (cliente, entregador, lojista, franquia, admin) since all users live in
 * the same `users` collection. The backend handles the dispatch logic.
 *
 * UX:
 *  • Always shows a generic success message (anti-enumeration).
 *  • Submits to POST /api/auth/forgot-password.
 */
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Informe seu e-mail.");
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await r.json().catch(() => ({}));
      // The backend always returns 200 to prevent enumeration.
      if (r.ok) {
        setSubmitted(true);
        toast.success(
          data.message || "Se a conta existir, enviaremos um link de recuperação."
        );
      } else {
        toast.error(data.detail || "Não foi possível enviar agora. Tente novamente.");
      }
    } catch {
      toast.error("Falha de conexão. Tente novamente em instantes.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-orange-100 p-7 sm:p-8">
          <div className="flex justify-center mb-5">
            <img loading="lazy" decoding="async" src={LOGO_URL} alt="GoFood" className="h-12 w-auto" />
          </div>

          {!submitted ? (
            <>
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-5 h-5 text-[#FF6B00]" />
                </div>
                <h1 className="font-heading text-2xl font-extrabold text-zinc-900">
                  Recuperar senha
                </h1>
                <p className="text-sm text-zinc-500 mt-1.5 leading-relaxed">
                  Informe o e-mail cadastrado. Enviaremos um link seguro para
                  você criar uma nova senha.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1"
                    autoComplete="email"
                    data-testid="forgot-email-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-primary to-orange-500 hover:from-primary/90 hover:to-orange-500/90 text-white font-semibold py-6 rounded-full"
                  disabled={loading}
                  data-testid="forgot-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    "Enviar link de recuperação"
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center" data-testid="forgot-success-state">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="font-heading text-xl font-extrabold text-zinc-900">
                Verifique seu e-mail
              </h1>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                Se o e-mail <strong>{email}</strong> estiver vinculado a uma
                conta GoFood, você receberá um link para redefinir sua senha
                em alguns instantes.
              </p>
              <p className="text-xs text-zinc-500 mt-3">
                O link expira em <strong>1 hora</strong> e só pode ser usado
                uma vez. Não esqueça de verificar a pasta de spam.
              </p>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF6B00] hover:underline"
              data-testid="forgot-back-to-login"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para o login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
